import React, { ChangeEvent, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { env } from "../config";

type FormDataType = {
  name: string;
  company: string;
  email: string;
  phone: string;
  sms_consent: boolean;
};

type FormErrorsType = Partial<Record<keyof FormDataType, string>>;

export function SmsOptInPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const emptyState: FormDataType = {
    name: "",
    company: "",
    email: "",
    phone: "",
    sms_consent: false,
  };

  const [formData, setFormData] = useState<FormDataType>(emptyState);
  const [errors, setErrors] = useState<FormErrorsType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrorsType = {};

    if (!formData.name.trim()) {
      newErrors.name = "Please enter your full name";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Please enter your email address";
    } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Please enter your phone number";
    } else if (!/^\+?[0-9\s\-()]{7,20}$/.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.sms_consent) {
      newErrors.sms_consent = "Please check the box to consent to SMS messaging";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    if (!validate()) {
      toast.error("Please fill in all required fields and accept the SMS consent.");
      return;
    }

    try {
      setIsLoading(true);
      toast.loading("Submitting...", { id: "smsOptIn" });

      await axios.post(`${env}/api/contact-page-emails`, {
        data: {
          Name: formData.name.trim(),
          Company: formData.company.trim(),
          Email: formData.email.trim(),
          Phone: formData.phone.trim(),
          Subject: "SMS Opt-In Request",
          Message: "User submitted SMS Opt-In form with consent granted.",
        },
      });

      toast.success("Successfully submitted! Thank you for opting in for SMS updates.", { id: "smsOptIn" });
      window.location.href = "/sms_opt_in_thank_you.html";
    } catch (error) {
      console.error("SMS Opt-In submit error:", error);
      toast.error("Failed to submit request. Please try again.", { id: "smsOptIn" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-[42rem] mx-auto px-5 py-10 pb-16 font-sans text-[#1f2328] bg-white leading-relaxed">
      <h1 className="text-3xl font-bold text-[#1f2328] mb-1">SMS Opt-In</h1>
      <p className="text-sm text-[#5a6169] mb-8">
        Netstratum Inc &middot; 24 Takolusa Dr, Holmdel, NJ 07733 &middot;{" "}
        <a href="https://netstratum.com/" className="text-[#0b5cab] hover:underline">
          netstratum.com
        </a>
      </p>

      <p className="mb-6 text-base">
        Netstratum Inc sends account-related SMS messages to its business customers. These include Two-Factor Authentication (2FA) messages for account security, Customer Care communications to address inquiries and provide support, and account notifications regarding order status and links to product rates. All messaging is transactional or informational.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-2 text-[#1f2328]">Opt in by web form</h2>
      <p className="mb-4 text-base">
        Complete the form below and check the consent box. The box is not pre-checked, and consent is not a condition of purchase.
      </p>

      <form onSubmit={handleSubmit} noValidate className="border border-[#d5dae0] rounded-lg p-6 my-6">
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-semibold text-[#1f2328] mb-1">
            Full name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full p-2.5 border rounded text-base ${
              errors.name ? "border-red-500" : "border-[#b9c0c8]"
            }`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="company" className="block text-sm font-semibold text-[#1f2328] mb-1">
            Company name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full p-2.5 border border-[#b9c0c8] rounded text-base"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-semibold text-[#1f2328] mb-1">
            Email address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full p-2.5 border rounded text-base ${
              errors.email ? "border-red-500" : "border-[#b9c0c8]"
            }`}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="phone" className="block text-sm font-semibold text-[#1f2328] mb-1">
            Mobile phone number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+1 555 555 5555"
            className={`w-full p-2.5 border rounded text-base ${
              errors.phone ? "border-red-500" : "border-[#b9c0c8]"
            }`}
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
        </div>

        <div className="flex gap-2.5 items-start mt-4">
          <input
            type="checkbox"
            id="sms_consent"
            name="sms_consent"
            checked={formData.sms_consent}
            onChange={handleChange}
            className="mt-1 h-4 w-4 rounded flex-shrink-0"
          />
          <label htmlFor="sms_consent" className="text-sm font-semibold text-[#1f2328] cursor-pointer">
            I consent to receive SMS notifications from Netstratum Inc
          </label>
        </div>
        {errors.sms_consent && <p className="text-red-500 text-xs mt-1">{errors.sms_consent}</p>}

        <p className="text-xs text-[#3d444c] mt-3 leading-relaxed">
          By checking this box, I consent to receive customer care, account notification, and two-factor authentication (2FA) SMS from Netstratum Inc. Reply STOP to opt-out; Reply HELP for support; Message &amp; data rates may apply; Messaging frequency may vary. Visit{" "}
          <a href="https://netstratum.com/privacy-policy" className="text-[#0b5cab] hover:underline">
            https://netstratum.com/privacy-policy
          </a>{" "}
          to see our privacy policy and{" "}
          <a href="https://netstratum.com/sms-terms-and-conditions" className="text-[#0b5cab] hover:underline">
            https://netstratum.com/sms-terms-and-conditions
          </a>{" "}
          for our Terms of Service. Mobile opt-in information and consent will not be shared with any third party or affiliate for marketing or promotional purposes.
        </p>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-5 px-6 py-2.5 text-base rounded bg-[#14263b] text-white hover:bg-[#1f3650] transition-colors"
        >
          {isLoading ? "Submitting..." : "Submit"}
        </button>
      </form>

      <h2 className="text-xl font-bold mt-8 mb-2 text-[#1f2328]">Opt in by text message</h2>
      <p className="mb-6 text-base">
        You may also opt in by texting the keyword <strong>SUBSCRIBE</strong> to <strong>+1 732-320-9170</strong>. You will receive a confirmation message describing the program, along with message frequency, rate, HELP and STOP information.
      </p>

      <h2 className="text-xl font-bold mt-8 mb-2 text-[#1f2328]">Program details</h2>
      <ul className="list-disc pl-5 mb-6 space-y-2 text-base">
        <li>
          <strong>Program name:</strong> Netstratum Inc account notifications
        </li>
        <li>
          <strong>Message types:</strong> 2FA messages, Customer Care communications, and account notifications regarding order status and links to product rates
        </li>
        <li>
          <strong>Message frequency:</strong> Message frequency varies based on account activity
        </li>
        <li>
          <strong>Cost:</strong> Message and data rates may apply. Charges are billed by, and payable to, your mobile service provider
        </li>
        <li>
          <strong>To opt out:</strong> Reply STOP to any message. You will receive one confirmation message and no further messages
        </li>
        <li>
          <strong>For help:</strong> Reply HELP to any message, email{" "}
          <a href="mailto:support@netstratum.com" className="text-[#0b5cab] hover:underline">
            support@netstratum.com
          </a>
          , or call +1 732-320-9170
        </li>
        <li>
          <strong>Carriers:</strong> Mobile carriers are not liable for delayed or undelivered messages
        </li>
      </ul>

      <h2 className="text-xl font-bold mt-8 mb-2 text-[#1f2328]">Your privacy</h2>
      <p className="mb-6 text-base">
        Mobile opt-in information and your consent to receive SMS notifications will not be shared, sold, rented, or disclosed to any third party or affiliate for marketing or promotional purposes. Full details are in our{" "}
        <a href="https://netstratum.com/privacy-policy" className="text-[#0b5cab] hover:underline">
          Privacy Policy
        </a>
        . Program terms are in our{" "}
        <a href="https://netstratum.com/sms-terms-and-conditions" className="text-[#0b5cab] hover:underline">
          SMS Terms &amp; Conditions
        </a>
        .
      </p>

      <h2 className="text-xl font-bold mt-8 mb-2 text-[#1f2328]">Contact</h2>
      <p className="text-base">
        Netstratum Inc<br />
        24 Takolusa Dr, Holmdel, NJ 07733<br />
        Email:{" "}
        <a href="mailto:support@netstratum.com" className="text-[#0b5cab] hover:underline">
          support@netstratum.com
        </a>
        <br />
        Phone: +1 732-320-9170
      </p>
    </div>
  );
}

export default SmsOptInPage;
